import {SerializedStyles} from '@emotion/react';
import {css, useTheme, IconButton, IconButtonProps, Typography} from '@mui/material';
import SvgIcon, {SvgIconProps} from '@mui/material/SvgIcon';
import React from 'react';
import {useIntl} from 'react-intl';
import {getIconComponentByName} from 'src/js/common/icons/icon_helpers';
import {GetStylesFunction} from 'src/js/common/types';
import {ColorUtils, TranslateUtils} from 'src/js/common/utils';
import {useMuiAnchor} from 'src/js/hooks';

import {DlMuiPopper} from './DlMuiPopper';

interface OwnProps extends IconButtonProps {
  iconName?: string;
  iconTitle: string;
  inputCss?: SerializedStyles;
  iconBackgroundCss?: SerializedStyles;
  svgIconInputCss?: SerializedStyles;
  svgIconProps?: SvgIconProps;
  isSelected?: boolean;
  color?: string;
  darkColor?: string;
}

type Props = OwnProps;

export const DlMuiDeleteButton: React.FC<Props> = (props) => {
  const {
    iconName = 'deleteIcon',
    inputCss,
    iconTitle,
    iconBackgroundCss,
    svgIconInputCss,
    svgIconProps,
    isSelected,
    color,
    darkColor,
    ...restProps
  } = props;
  const theme = useTheme();
  const intl = useIntl();
  const styles = getStyles({
    theme,
    params: {inputCss, iconBackgroundCss, svgIconInputCss, isSelected, color, darkColor},
  });

  const translatedIconTitle = intl && TranslateUtils.translate(iconTitle, intl);
  const hasTooltip = !!translatedIconTitle;
  const {mouseEventEnter, mouseEventLeave, open, anchorEl} = useMuiAnchor(hasTooltip);
  let RenderedIconComponent;
  if (iconName) {
    RenderedIconComponent = getIconComponentByName(iconName);
  }

  return (
    <>
      <IconButton onMouseEnter={mouseEventEnter} onMouseLeave={mouseEventLeave} css={styles.iconButton} {...restProps}>
        <div css={styles.iconBackground}>
          <SvgIcon css={styles.svgIcon} component={RenderedIconComponent} viewBox='0 0 16 16' {...svgIconProps} />
        </div>
      </IconButton>
      <DlMuiPopper hasTooltip={hasTooltip} anchorEl={anchorEl} open={open}>
        <Typography variant={'popper'}>{translatedIconTitle}</Typography>
      </DlMuiPopper>
    </>
  );
};

DlMuiDeleteButton.displayName = 'DlMuiDeleteButton';

const getStyles: GetStylesFunction = (options) => {
  const RED = options?.theme?.palette.custom.red['400'];
  const DARK_RED = options?.theme?.palette.custom.red['500'];
  const color = options?.params?.color ?? RED;
  const darkColor = options?.params?.darkColor ?? DARK_RED;
  const getBackgroundColor = (hex) => {
    const rgb = ColorUtils.hexToRgb(hex);
    return ColorUtils.rgbToRgbaString(rgb as any, 0.4);
  };
  const background = getBackgroundColor(color);
  const darkBackground = getBackgroundColor(darkColor);
  return {
    iconButton: css(
      {
        cursor: 'pointer',
        padding: 3,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 14,
      },
      options?.params?.inputCss
    ),
    iconBackground: css(
      {
        'cursor': 'pointer',
        'width': 16,
        'height': 16,
        'borderRadius': 8,
        'background': options?.params?.isSelected ? darkBackground : background,
        '&:hover': {
          background: darkBackground,
        },
      },
      options?.params?.iconBackgroundCss
    ),
    svgIcon: css(
      {
        'cursor': 'pointer',
        'fontSize': '1em',
        'verticalAlign': 'top',
        'fill': options?.params?.isSelected ? darkColor : color,
        '&:hover': {
          fill: darkColor,
        },
      },
      options?.params?.svgIconInputCss
    ),
  };
};
